import {
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  //Paper,
  Box,
  Checkbox,
  Button,
  Chip,
} from "@mui/material";
import GenericModal from "../../Modal/GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import { ChangeEvent, useContext, useState, useEffect } from "react";
import { AUTODELRULATE_AD_LINK } from "../../Utils/Constants";
import { CacheContext } from "../../Context/cacheContext";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import { getData } from "../../Services/getData";
import {
  WEBSITE_INTEGRATIONS,
  WebsiteModel,
  isVehicleSyncedOnWebsite,
} from "../../Utils/websiteIntegrations";
import { getActiveLocations } from "../../Utils/utils";
import { urlEnum } from "../../Utils/urlEnum";
//import InsertLinkIcon from "@mui/icons-material/InsertLink";

export interface ModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  vehicle: any | null;
  setVehicle: (data: any) => void;
  getDataRefresh: () => void;
}

export default function PostAdsIntegrationModal(props: ModalProps) {
  const { open, setOpen, vehicle, setVehicle, getDataRefresh } = props;
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [newCar, setNewCar] = useState({});
  const [websiteLocations, setWebsiteLocations] = useState<any>({});
  const [data, setData] = useState<Record<string, boolean>>({});
  const [availableWebsites, setAvailableWebsites] = useState<WebsiteModel[]>(
    []
  );
  const [confirmDeactivate, setConfirmDeactivate] = useState<{
    open: boolean;
    website: WebsiteModel | null;
  }>({ open: false, website: null });
  const cache = useContext(CacheContext)?.cache;
  const locations = getActiveLocations(cache?.locations);

  /**
   * Fetch available websites for the vehicle's location
   */
  useEffect(() => {
    if (open && vehicle?.locatie_administrativa_id) {
      getData(
        `${urlEnum.websitesIntegration}/available/${vehicle.locatie_administrativa_id}`
      )
        .then((response: any) => {
          // Filter WEBSITE_INTEGRATIONS to only include those available for this location
          const availableTypes =
            response?.data?.data?.map((int: any) => int?.type) || [];
          const filtered = WEBSITE_INTEGRATIONS.filter(
            (website) =>
              availableTypes?.includes(website.code) ||
              website.code === "autodelrulate"
          );
          setAvailableWebsites(filtered);
        })
        .catch(() => {
          // Fall back to all websites if API fails
          setAvailableWebsites(WEBSITE_INTEGRATIONS);
        });
    }
  }, [open, vehicle?.locatie_administrativa_id]);

  /**
   * Seed each website's switch state from whether the vehicle already
   * has an advert on it, so the per-card sync button and the switch
   * itself reflect reality immediately, not just after a manual toggle.
   */
  useEffect(() => {
    if (!open || !availableWebsites.length) return;
    const initialData: Record<string, boolean> = {};
    availableWebsites.forEach((website) => {
      initialData[website.code] = isVehicleSyncedOnWebsite(vehicle, website.code);
    });
    setData(initialData);
  }, [open, vehicle, availableWebsites]);

  /**
   *
   * @param url
   */
  const syncWebsiteAd = async (website: WebsiteModel) => {
    try {
      if (!website.url) return;

      const vehicleId = vehicle
        ? vehicle["id" as keyof typeof vehicle]
        : "null";

      // If URL already has ?, split and insert vehicle ID
      const url = website.url.includes("?")
        ? website.url.replace("?", `/${vehicleId}?`)
        : `${website.url}/${vehicleId}`;
      const isNewCar = Object.assign(newCar)[website.code];

      //get selected locations for this website
      const selectedLocations = websiteLocations[website.code] || [];
      const selectedLocationIds = selectedLocations.map(
        (loc: any) => loc.id
      );

      const response = await postData(url, {
        isNewCar,
        selectedLocationIds,
      });

      if (response?.status == 200 && response?.data) {
        setVehicle(response?.data?.vehicle);
        setShouldRefresh(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  /**
   * Maps a website to its site-specific deactivate request, since each
   * integration's backend route shape differs (route vs body vehicleId,
   * and Bestauto needing the full rebuild payload).
   */
  const buildDeactivateRequest = (website: WebsiteModel) => {
    const vehicleId = vehicle ? vehicle["id" as keyof typeof vehicle] : null;
    switch (website.code) {
      case "autovit":
        return { url: `${urlEnum.autovit}/${vehicleId}/deactivate`, body: {} };
      case "bestauto":
        return {
          url: `${urlEnum.bestauto}/${vehicleId}/deactivate`,
          body: {
            isNewCar: Object.assign(newCar)[website.code],
            selectedLocationIds: (websiteLocations[website.code] || []).map(
              (loc: any) => loc.id
            ),
          },
        };
      case "plusauto":
        return { url: `${urlEnum.plusAuto}/${vehicleId}/deactivate`, body: {} };
      case "xauto":
        return { url: urlEnum.xautoDeactivate, body: { vehicleId } };
      case "autodelrulate":
        return { url: `/api/vehicule/deactivateOnAutodelrulate/${vehicleId}`, body: {} };
      default:
        return null;
    }
  };

  const deactivateWebsiteAd = async (website: WebsiteModel) => {
    const request = buildDeactivateRequest(website);
    if (!request) return;

    try {
      const response = await updateData(request.url, request.body);
      if (response?.data?.vehicle) {
        setVehicle(response.data.vehicle);
      }
      setData({ ...data, [website.code]: false });
    } catch (e) {
      console.log(e);
      setData({ ...data, [website.code]: true });
    } finally {
      setConfirmDeactivate({ open: false, website: null });
    }
  };

  /**
   * Cancelling the confirm dialog (No button or closing it any other way)
   * means the deactivation never happened, so the switch must go back on.
   */
  const cancelDeactivate = () => {
    if (confirmDeactivate.website) {
      setData({ ...data, [confirmDeactivate.website.code]: true });
    }
    setConfirmDeactivate({ open: false, website: null });
  };

  /**
   * Returns the raw advert data/URL for a website only while it's still
   * active — once a site's deactivation is recorded (deactivated_at inside
   * its *_advert_data JSON, or autodelrulate_deactivated_at for the
   * Autodelrulate case), the link is suppressed rather than pointing at a
   * listing that's no longer live. The underlying stored data is untouched
   * either way — this only affects what gets displayed.
   */
  const getAdvertUrlIfActive = (website: WebsiteModel, vehicle: any) => {
    if (!vehicle) return null;

    if (website.code === "autodelrulate") {
      if (vehicle["autodelrulate_deactivated_at" as keyof typeof vehicle]) {
        return null;
      }
      return vehicle["slug" as keyof typeof vehicle]
        ? JSON.stringify({
            advert_url:
              AUTODELRULATE_AD_LINK + vehicle["slug" as keyof typeof vehicle],
          })
        : null;
    }

    const rawAdvertData = vehicle[
      (website.code.toLowerCase() + "_advert_data") as keyof typeof vehicle
    ];
    if (!rawAdvertData) return null;

    const advertData =
      typeof rawAdvertData === "string"
        ? JSON.parse(rawAdvertData)
        : rawAdvertData;
    return advertData?.deactivated_at ? null : rawAdvertData;
  };

  /**
   *
   * @param advertUrlData
   * @returns
   */
  const renderAdvertUrl = (advertUrlData: any) => {
    const advertUrl = typeof advertUrlData === "string" ? JSON.parse(advertUrlData) : advertUrlData;
    const advertUrlParsed = advertUrl["advert_url"].split(",");
    return advertUrlParsed.map((url: string) => {
      return (
        <a
          style={{
            textDecoration: "none",
            color: "black",
            cursor: "pointer",
            wordBreak: "break-word",
          }}
          rel="noreferrer"
          target="_blank"
          href={url}
        >
          {url}
        </a>
      );
    });
  };

  /**
   *
   */
  const onClose = () => {
    setOpen(false);
    if (shouldRefresh) {
      // sa faca refresh la date doar daca a fost postat un anunt
      getDataRefresh();
      setShouldRefresh(false);
    }
    setNewCar({});
  };

  return (
    <>
      <GenericModal
        open={open}
        onClose={onClose}
        title={Vocabulary.postAd}
        subtitle={
          vehicle?.locatie_administrativa_r?.nume ? (
            <Chip
              label={`${Vocabulary.vehicleLocation}: ${vehicle.locatie_administrativa_r.nume}`}
              color="primary"
              size="small"
              style={{ marginTop: "0.5rem" }}
            />
          ) : null
        }
        buttons={
          <Grid container spacing={3} style={{ padding: "1rem" }}>
            <Grid item md={12} xs={12}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={onClose}
              >
                {Vocabulary.cancel}
              </Button>
            </Grid>
          </Grid>
        }
        children={
          <Grid container spacing={2}>
            {availableWebsites.map((website: WebsiteModel) => {
              const advertUrl = getAdvertUrlIfActive(website, vehicle);

              // const isPostableOnWebsite = vehicle
              //   ? vehicle[
              //       ("is_postable_on_" +
              //         website.code.toLowerCase()) as keyof typeof vehicle
              //     ]
              //   : null;
              return (
                <>
                  <Grid item md={12} xs={12}>
                    <Box
                      style={{
                        padding: "1rem",
                        borderRadius: "7px",
                        backgroundColor: "#fff !important",
                        boxShadow:
                          "rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px",
                        position: "relative",
                      }}
                    >
                      {/* Website Selection - Main Switch + per-site sync action */}
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <FormControlLabel
                          label={
                            <Typography
                              style={{ fontSize: "1.3rem", fontWeight: 600 }}
                            >
                              {website.name}
                            </Typography>
                          }
                          control={
                            <Switch
                              // disabled={!isPostableOnWebsite}
                              checked={!!data[website.code]}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                const checked = e.target.checked;
                                if (!checked && data[website.code]) {
                                  setData({ ...data, [website.code]: false });
                                  setConfirmDeactivate({ open: true, website });
                                  return;
                                }
                                setData({ ...data, [website.code]: checked });
                              }}
                            />
                          }
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ color: "white" }}
                          disabled={!data[website.code]}
                          onClick={() => syncWebsiteAd(website)}
                        >
                          {Vocabulary.sync}
                        </Button>
                      </Box>

                      {/* Divider */}
                      <Box
                        style={{
                          borderTop: "1px solid #e0e0e0",
                          margin: "0.75rem 0",
                        }}
                      />

                      {/* Options Section */}
                      <div style={{ paddingLeft: "0.5rem" }}>
                        {/* New Car Checkbox */}
                        <div style={{ marginBottom: "0.5rem" }}>
                          <FormControlLabel
                            label={
                              <Typography style={{ fontSize: "0.95rem" }}>
                                {Vocabulary.newCar}
                              </Typography>
                            }
                            control={
                              <Checkbox
                                defaultChecked={false}
                                onClick={(event: any) => {
                                  setNewCar({
                                    [website.code]: event.target.checked,
                                  });
                                }}
                              />
                            }
                          />
                        </div>

                        {/* Locations Section */}
                        {locations?.length > 0 && (
                          <div>
                            <Typography
                              style={{
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                color: "#666",
                                marginBottom: "0.5rem",
                                //  textTransform: "uppercase",
                                // letterSpacing: "0.5px"
                              }}
                            >
                              {Vocabulary.locationsForPhotos}:
                            </Typography>
                            <Box
                              style={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fill, minmax(150px, 1fr))",
                                gap: "0.25rem",
                                padding: "0.5rem",
                                backgroundColor: "#f9f9f9",
                                borderRadius: "4px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              {locations.map((location: any) => {
                                return (
                                  <FormControlLabel
                                    key={location.id}
                                    label={
                                      <Typography
                                        style={{ fontSize: "0.9rem" }}
                                      >
                                        {location.nume}
                                      </Typography>
                                    }
                                    control={
                                      <Switch
                                        defaultChecked={false}
                                        size="small"
                                        onChange={(e) => {
                                          const currentLocations =
                                            websiteLocations[website.code] ||
                                            [];
                                          const updatedLocations = e.target
                                            .checked
                                            ? [...currentLocations, location]
                                            : currentLocations.filter(
                                                (loc: any) =>
                                                  loc.id !== location.id
                                              );
                                          setWebsiteLocations({
                                            ...websiteLocations,
                                            [website.code]: updatedLocations,
                                          });
                                        }}
                                      />
                                    }
                                    style={{ margin: 0 }}
                                  />
                                );
                              })}
                            </Box>
                          </div>
                        )}
                      </div>
                      {
                        //se afiseaza link-ul catre anunt doar daca el exista si nu se poate reposta un nou anunt
                        //adica inca nu a expirat anuntul curent
                        advertUrl && (
                          <>
                            <Typography
                              style={{
                                fontSize: "1rem",
                                padding: "0.5rem",
                                marginTop: "1rem",
                              }}
                            >
                              {Vocabulary.advertLink}{" "}
                            </Typography>
                            <Typography
                              style={{
                                fontSize: "1rem",
                                fontWeight: "600",
                                width: "100%",
                                padding: "0 0.5rem",
                              }}
                            >
                              {renderAdvertUrl(advertUrl)}
                            </Typography>
                          </>
                        )
                      }
                    </Box>
                  </Grid>
                </>
              );
            })}
          </Grid>
        }
      />
      <GenericModal
        open={confirmDeactivate.open}
        onClose={cancelDeactivate}
        title={Vocabulary.deactivate}
      >
        <Typography style={{ textAlign: "center", fontSize: 20, fontWeight: 700 }}>
          {Vocabulary.adOnPlatform}{" "}
          <span style={{ color: "#e53935" }}>
            {confirmDeactivate.website?.name}
          </span>
        </Typography>
        <p style={{ textAlign: "center", fontSize: 15, color: "#666", marginTop: "0.25rem" }}>
          {Vocabulary.deactivateConfirmMessage}
        </p>
        <Grid container spacing={2} style={{ padding: 10 }}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={cancelDeactivate}
            >
              {Vocabulary.no}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              variant="contained"
              fullWidth
              style={{ color: "white", backgroundColor: "#e53935" }}
              onClick={() =>
                confirmDeactivate.website &&
                deactivateWebsiteAd(confirmDeactivate.website)
              }
            >
              {Vocabulary.yes}
            </Button>
          </Grid>
        </Grid>
      </GenericModal>
    </>
  );
}
